import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock3, XCircle, MessageSquareText, Loader2, Award, Radar } from "lucide-react";
import { Button } from "@/components/ui/button";
import TicketView from "@/components/TicketView";
import PrintButtons from "@/components/PrintButtons";
import { api, errMsg } from "@/lib/api";
import { money, fmtTime } from "@/lib/format";

function LiveStatus({ code }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/public/track/${code}`).then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [code]);
  if (!d || !d.live) return null;
  const board = d.timeline[d.from_idx];
  const msg = board.actual ? `Your bus arrived at ${board.city} at ${fmtTime(board.actual)}` : d.from_idx === 0 && board.status === "departed" ? `Your bus left ${board.city} at ${fmtTime(board.actual)}` : `Your bus is expected at ${board.city} at ${fmtTime(board.eta)}${d.delay_min > 5 ? ` (${d.delay_min} min late)` : ""}`;
  return (
    <div className="rise flex flex-col gap-3 rounded-2xl border border-blue-200 bg-white p-4 sm:flex-row sm:items-center" data-testid="ticket-live-status">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-blue-50 text-[#0052FF]"><Radar className="h-5 w-5" /></span>
      <div className="flex-1"><div className="label-xs">Live bus status</div><div className="font-semibold" data-testid="ticket-live-message">{msg}</div><div className="text-xs text-slate-500">Next stop: {d.next_stop ? `${d.next_stop.city} ~${fmtTime(d.next_stop.eta)}` : "arrived"}</div></div>
      <Button size="sm" className="rounded-full bg-[#0052FF] hover:bg-[#0041CC]" onClick={() => nav(`/track/${code}`)} data-testid="ticket-track-button"><Radar className="h-4 w-4" />Track my bus</Button>
    </div>
  );
}

const BANNER = {
  confirmed: { icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-200 text-emerald-800", title: "Booking confirmed" },
  boarded: { icon: CheckCircle2, cls: "bg-sky-50 border-sky-200 text-sky-800", title: "Boarded - have a safe journey" },
  reserved: { icon: Clock3, cls: "bg-amber-50 border-amber-200 text-amber-800", title: "Seats reserved - payment pending" },
  pending_payment: { icon: Clock3, cls: "bg-amber-50 border-amber-200 text-amber-800", title: "Confirming your payment with Hubtel..." },
  payment_failed: { icon: XCircle, cls: "bg-rose-50 border-rose-200 text-rose-800", title: "Payment not completed - seats released" },
  cancelled: { icon: XCircle, cls: "bg-rose-50 border-rose-200 text-rose-800", title: "Booking cancelled" },
  expired: { icon: XCircle, cls: "bg-slate-100 border-slate-200 text-slate-700", title: "Reservation expired" },
};

export default function TicketPage() {
  const { code } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [checking, setChecking] = useState(false);
  useEffect(() => { api.get(`/bookings/${code}`).then((r) => setB(r.data)).catch((e) => setErr(errMsg(e))); }, [code]);
  useEffect(() => {
    if (b?.status !== "pending_payment") return;
    // Fast 4s auto-check; for tickets started more than 1 hour ago slow to every 5 minutes.
    const ageMs = b.created_at ? Date.now() - new Date(b.created_at).getTime() : 0;
    const delay = ageMs > 3600000 ? 300000 : 4000;
    const t = setTimeout(async () => {
      try { await api.get(`/payments/${b.code}/status`); setB((await api.get(`/bookings/${b.code}`)).data); } catch { setB({ ...b }); }
    }, delay);
    return () => clearTimeout(t);
  }, [b]);

  const iPaid = async () => {
    setChecking(true);
    try {
      const { data } = await api.post(`/payments/${b.code}/check`);
      const fresh = (await api.get(`/bookings/${b.code}`)).data;
      setB(fresh);
      if (fresh.status === "confirmed") toast.success("Payment confirmed - your ticket is issued");
      else if (data.status === "pending_payment") toast.message("We haven't received the payment from Hubtel yet. If you've approved it, please try again shortly.");
      else toast.error("Payment was not completed");
    } catch (e) { toast.error(errMsg(e)); } finally { setChecking(false); }
  };
  const sms = async () => {
    try {
      const { data } = await api.post(`/bookings/${b.code}/sms`);
      toast.success(`Ticket link sent by SMS to ${data.sent_to.join(", ")}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (err) return <div className="mx-auto max-w-md p-10 text-center" data-testid="ticket-not-found"><XCircle className="mx-auto h-10 w-10 text-rose-400" /><p className="mt-2 font-semibold">{err}</p><Link className="text-[#0052FF] underline" to="/find">Try another code</Link></div>;
  if (!b) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;
  const s = BANNER[b.status] || BANNER.confirmed;

  return (
    <div className="hero-bg min-h-full">
      <div className="mx-auto max-w-4xl space-y-5 px-4 py-6 sm:px-6">
        <div className={`rise flex flex-col gap-3 rounded-2xl border p-5 sm:flex-row sm:items-center ${s.cls}`} data-testid="ticket-status-banner">
          <s.icon className="h-8 w-8 shrink-0" />
          <div className="flex-1">
            <div className="font-display text-xl font-extrabold">{s.title}</div>
            <div className="text-sm">Booking code <b className="font-mono tracking-widest" data-testid="ticket-booking-code">{b.code}</b> · {b.passengers.length} seat{b.passengers.length > 1 ? "s" : ""} · {money(b.total)}</div>
            {b.status === "reserved" && <div className="mt-1 text-sm">Pay at any GPRTU counter before departure by quoting this code.</div>}
          </div>
          {b.miles_earned > 0 && <span className="chip bg-white text-amber-700" data-testid="ticket-miles-earned"><Award className="h-3.5 w-3.5" />+{b.miles_earned} miles</span>}
          {["pending_payment", "payment_failed"].includes(b.status) && (
            <Button size="sm" disabled={checking} onClick={iPaid} className="rounded-full bg-[#0052FF] font-bold text-white hover:bg-[#0041CC]" data-testid="ticket-ipaid-button">
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}I have paid - check now
            </Button>
          )}
        </div>
        {["confirmed", "boarded", "reserved"].includes(b.status) && <LiveStatus code={b.code} />}
        <div className="flex flex-wrap items-center gap-2">
          <PrintButtons testPrefix="ticket-print">{(f) => <TicketView booking={b} format={f} />}</PrintButtons>
          <Button size="sm" variant="outline" className="rounded-full btn-press" onClick={sms} data-testid="ticket-sms-button"><MessageSquareText className="h-4 w-4" />Send to my phone</Button>
          <Button size="sm" variant="outline" className="rounded-full btn-press" asChild><Link to={`/track/${b.code}`} data-testid="ticket-track-link"><Radar className="h-4 w-4" />Track bus</Link></Button>
        </div>
        <TicketView booking={b} format="a4" />
      </div>
    </div>
  );
}
